import React, { useState, useContext } from "react";
import { styled } from "@mui/material/styles";
import {
  AppBar,
  Typography,
  Container,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TextField,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DisableHotkeyContext from "../contexts/DisableHotkeyContext";

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderBottom: `5px solid ${theme.palette.secondary.main}`,
  height: 100,
  alignItems: "center",
  justifyContent: "space-around",
  width: "100%",
}));

const StyledToolbar = styled(Toolbar)({
  justifyContent: "space-around",
});

const StyledAccordion = styled(Accordion)({
  boxShadow: "none",
  borderRadius: 0,
  root: {
    "&$expanded": {
      margin: "auto",
    },
  },
});

const StyledTextField = styled(TextField)({
  backgroundColor: "white",
  borderRadius: 4,
  width: 300,
  marginRight: 10,
  // border: "1px solid #ccc",
  outline: "none",
  position: "relative",
  fontFamily: "inherit",
  fontSize: "14px",
});

const StyledButton = styled(Button)({
  flexShrink: 0,
  fontFamily: "inherit",
  fontSize: "14px",
  padding: "16px",
  marginLeft: "10px",
  textShadow: "rgba(0, 0, 0, 0.05) 0px 1px 2px",
  color: "white",
  border: "none",
  boxShadow: "rgba(0, 0, 0, 0.05) 0px 0px 0px 1px inset, rgba(59, 69, 79, 0.05) 0px 1px 0px",
  background: "linear-gradient(#2da0fa, #3158ee) #2f7cf4",
  cursor: "pointer",
  borderRadius: "4px",
  "&:hover": {
    boxShadow: "rgba(0, 0, 0, 0.3) 0px 0px 0px 1px inset, rgba(59, 69, 79, 0.3) 0px 2px 4px",
    background: "linear-gradient(#3cabff, #4063f0) #3d84f7",
  },
});

const StyledForm = styled("form")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

interface MenuPageItem {
  name: string;
  path?: string;
  submenu?: MenuPageItem[];
}

interface MenuProps {
  pages: MenuPageItem[];
}

interface SubMenuProps {
  pages: MenuPageItem[];
  anchorElNav: HTMLElement | null;
  setAnchorElNav: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  open: boolean;
}

export const SubMenu: React.FC<SubMenuProps> = ({ pages, anchorElNav, setAnchorElNav, open }: SubMenuProps) => {
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };
  const location = useLocation();
  return (
    <Menu
      id="menu-appbar"
      anchorEl={anchorElNav}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      open={open}
      onClose={handleCloseNavMenu}
    >
      {pages.map((page) => {
        if (page.submenu) {
          return (
            <div key={page.name}>
              <StyledAccordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                  <Typography>{page.name}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {page.submenu.map((subpage) => (
                    <MenuItem
                      dense
                      className="hamburger-submenu-item"
                      key={subpage.name}
                      component={RouterLink}
                      to={subpage.path || ""}
                      onClick={() => {
                        handleCloseNavMenu();
                      }}
                      selected={location.pathname === subpage.path}
                    >
                      {subpage.name}
                    </MenuItem>
                  ))}
                </AccordionDetails>
              </StyledAccordion>
            </div>
          );
        } else {
          return (
            <MenuItem
              key={page.name}
              component={RouterLink}
              to={page.path || ""}
              onClick={() => {
                handleCloseNavMenu();
              }}
              selected={location.pathname === page.path}
            >
              {page.name}
            </MenuItem>
          );
        }
      })}
    </Menu>
  );
};

export const HamburgerMenu: React.FC<MenuProps> = ({ pages }: MenuProps) => {
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  return (
    <>
      <IconButton
        size="large"
        aria-label="hamburger menu"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleOpenNavMenu}
      >
        <MenuIcon />
      </IconButton>
      <SubMenu open={Boolean(anchorElNav)} pages={pages} anchorElNav={anchorElNav} setAnchorElNav={setAnchorElNav} />
    </>
  );
};

export const pages = [
  {
    name: "Search",
    path: "/Search",
  },
  {
    name: "Favourites",
    path: "/Favourites",
  },
  {
    name: "Logic Tree",
    path: "/LogicTree",
  },
];

const NavBar: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { setDisableHotkey } = useContext(DisableHotkeyContext);
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <React.StrictMode>
      <StyledAppBar position="static">
        <Container maxWidth={false}>
          <StyledToolbar disableGutters>
            <HamburgerMenu pages={pages} />
            <Box sx={{ flexGrow: 1, display: "flex" }} />
            <RouterLink style={{ textDecoration: "none" }} to="/">
              <Typography variant="h1" color={"white"}>
                Weka
              </Typography>
            </RouterLink>
            {location.pathname.includes("/Search") ? <Box sx={{ flexGrow: 1.05 }} /> : <Box sx={{ flexGrow: 0.56 }} />}
            {!location.pathname.includes("/Search") && (
              <StyledForm
                onSubmit={(event) => {
                  event.preventDefault();
                  setDisableHotkey(false);
                  navigate(`/Search?q=${encodeURIComponent(searchTerm.trim())}`);
                }}
                onFocus={() => setDisableHotkey(true)}
                onBlur={() => setDisableHotkey(false)}
              >
                <StyledTextField color="primary" onChange={(e) => setSearchTerm(e.target.value)} />
                <StyledButton color="warning" variant="contained" type="submit">
                  Search
                </StyledButton>
              </StyledForm>
            )}
          </StyledToolbar>
        </Container>
      </StyledAppBar>
    </React.StrictMode>
  );
};

export default NavBar;
